import { ButtonReset, Stack, Text } from '@koob/margaret';
import {
  KoobTooltip,
  KoobInputDateRangeField,
  KoobInputStepperField,
} from '@koob/ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import TimeRangePickerField from '../TimeRangePickerField';
import TextField from '../TextField';
import { Field } from 'formik';
import ConditionRecapField from './CancelConditionRecapField';

const RemoveCondition = styled(ButtonReset).attrs({ type: 'button' })`
  border: solid 1px transparent;
  &:hover {
    color: red;
  }
`;

const DuplicateCondition = styled(ButtonReset).attrs({ type: 'button' })`
  border: solid 1px transparent;
  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const CancelConditionItemField = ({
  index,
  name,
  showNumberOfNights,
  onDelete,
  onDuplicate,
  timezone = 'UTC',
  currency,
  disabled = false,
}) => {
  const { t } = useTranslation('cancellationPolicies');

  return (
    <div className="bg-white border border-gray-200 rounded-2xl" key={index}>
      <div className="flex justify-between items-center px-5 py-4 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />
          <Text color="textLight">{t(`Condition ${index + 1}`)}</Text>
        </div>
        {!disabled && (
          <Stack gutterSize={0.75}>
            <DuplicateCondition
              type="button"
              title={t('duplicate')}
              onClick={() => onDuplicate(index)}
            >
              <i className="fa-regular fa-copy text-lg" />
            </DuplicateCondition>
            <RemoveCondition type="button" onClick={() => onDelete(index)}>
              <i className="fa-regular fa-trash text-lg" />
            </RemoveCondition>
          </Stack>
        )}
      </div>

      <div className="p-5">
        <Stack
          alignY="center"
          alignX="spacr-between"
          gutterSize={4}
          size="full"
        >
          <KoobInputDateRangeField
            name={`${name}.${index}.period`}
            label={t('period')}
            isDateTime={true}
            updateOnlyDate={true}
            clearable={false}
            disabled={disabled}
          />
          <TimeRangePickerField
            name={`${name}.${index}.period`}
            timezone={timezone}
            label={t('timeRange')}
            disabled={disabled}
          />
        </Stack>
        <hr className="border-gray-200 my-4" />
      </div>

      <div className="p-5 flex flex-wrap items-start gap-x-10 gap-y-4">
        <div className="flex items-start space-x-4">
          <div className="w-[6.25rem]">
            <TextField
              type="number"
              min="0"
              name={`${name}.${index}.deduction`}
              label={t('priceDeduction')}
              disabled={disabled}
            />
          </div>
          <div className="flex flex-col space-y-2">
            <label className="text-lg font-bold">{t('isPercent')}</label>
            <Field
              type="checkbox"
              name={`${name}.${index}.isPercent`}
              className="accent-orange-500"
              disabled={disabled}
            />
          </div>
        </div>

        {showNumberOfNights && (
          <KoobInputStepperField
            name={`${name}.${index}.numberOfNight`}
            label={
              <span className="flex items-center gap-2">
                <span>{t('numberOfNight')}</span>
                <KoobTooltip
                  tip={t('isByNightTooltip')}
                  color="purple"
                  hasArrow={false}
                  position="top"
                >
                  <i className="fa-sharp fa-regular fa-circle-info" />
                </KoobTooltip>
              </span>
            }
            min={0}
            disabled={disabled}
          />
        )}

        <div className="flex items-start gap-4">
          <KoobInputStepperField
            name={`${name}.${index}.daysFrom`}
            label={`${t('from')} (${t('days')})`}
            min={0}
            disabled={disabled}
          />
          <KoobInputStepperField
            name={`${name}.${index}.daysTo`}
            label={`${t('to')} (${t('days')})`}
            min={0}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="p-5">
        <ConditionRecapField name={name} index={index} currency={currency} />
      </div>
    </div>
  );
};

export default CancelConditionItemField;
